/**
 * useResumeForm.js
 *
 * Owns ALL resume form state and mutations.
 * Extracted from CompleteEnhancedResumeBuilder to eliminate the 300-line
 * state/handler block living inside a render component.
 *
 * Returns a stable `handlers` object (all callbacks are memoized) so
 * child components never re-render due to handler identity changes.
 */

import { useState, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { computeAtsScore } from '../utils/atsEngine';
import { downloadResumePDF, previewResumePDF } from '../components/resume/pdf/pdfExportHelpers.jsx';
import { trackResumeDownload } from '../lib/supabase';
import { useResumeAutosave, loadDraft, clearDraft } from './useResumeAutosave';
import { makeDefaultFormData, uid, SKILL_CATEGORIES, TOOL_CATEGORIES } from '../components/resume/resumeConstants';
import { 
  validatePersonalInfo, 
  sanitizeInput, 
  detectIssues,
  MAX_LENGTHS 
} from '../utils/formValidation';

// Safe string helper for array items
const toStr = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    const extracted = val.text || val.name || val.title || val.value || val.label || '';
    return typeof extracted === 'string' ? extracted : '';
  }
  return '';
};

export function useResumeForm(userEmail = '') {
  console.log('[useResumeForm] Initializing with email:', userEmail);
  const defaultData = makeDefaultFormData(userEmail);

  // Safe draft loading with validation & sanitization
  const loadSafeDraft = () => {
    console.log('[useResumeForm] Loading draft...');
    try {
      const draft = loadDraft();
      if (!draft) {
        console.log('[useResumeForm] No draft found, using defaults');
        return defaultData;
      }
      
      console.log('[useResumeForm] Draft loaded, validating and sanitizing...');
      // Validate draft has all required keys
      const requiredKeys = ['personal', 'summary', 'coreCompetencies', 'technicalSkills', 'tools', 'experience', 'projects', 'certifications', 'education'];
      for (const key of requiredKeys) {
        if (!(key in draft)) {
          console.warn(`[useResumeForm] Draft missing required key: ${key}, using defaults`);
          return defaultData;
        }
      }
      
      // Ensure all section arrays exist and are valid arrays
      const allArraySections = [
        'experience', 'projects', 'certifications', 'education', 'coreCompetencies',
        'achievements', 'awards', 'languages', 'volunteer', 'publications',
        'openSource', 'memberships', 'portfolio', 'patents', 'aiInjectedKeywords'
      ];
      for (const key of allArraySections) {
        if (draft[key] && !Array.isArray(draft[key])) {
          draft[key] = defaultData[key] || [];
        } else if (!draft[key]) {
          draft[key] = defaultData[key] || [];
        }
      }

      // Sanitize coreCompetencies to be string primitives only
      draft.coreCompetencies = (draft.coreCompetencies || []).map(toStr).filter(Boolean);

      // Sanitize technicalSkills to have valid string arrays for all default categories
      draft.technicalSkills = { ...defaultData.technicalSkills, ...(draft.technicalSkills || {}) };
      Object.keys(draft.technicalSkills).forEach(cat => {
        if (!Array.isArray(draft.technicalSkills[cat])) {
          draft.technicalSkills[cat] = [];
        } else {
          draft.technicalSkills[cat] = draft.technicalSkills[cat].map(toStr).filter(Boolean);
        }
      });

      // Sanitize tools to have valid string arrays for all default categories
      draft.tools = { ...defaultData.tools, ...(draft.tools || {}) };
      Object.keys(draft.tools).forEach(cat => {
        if (!Array.isArray(draft.tools[cat])) {
          draft.tools[cat] = [];
        } else {
          draft.tools[cat] = draft.tools[cat].map(toStr).filter(Boolean);
        }
      });

      // Sanitize experience achievements to be objects { _id, text }
      draft.experience = (draft.experience || []).map(exp => {
        let achs = exp.achievements;
        if (!Array.isArray(achs)) {
          achs = [{ _id: uid(), text: '' }];
        } else {
          achs = achs.map(a => {
            if (typeof a === 'string') return { _id: uid(), text: a };
            if (a && typeof a === 'object') return { _id: a._id || uid(), text: toStr(a.text) };
            return { _id: uid(), text: '' };
          });
        }
        return { ...exp, achievements: achs };
      });

      // Sanitize projects responsibilities
      draft.projects = (draft.projects || []).map(proj => {
        let resps = proj.responsibilities;
        if (!Array.isArray(resps)) {
          if (typeof resps === 'string' && resps.trim()) {
            resps = resps.split('\n').map(t => ({ _id: uid(), text: t.trim() }));
          } else {
            resps = [{ _id: uid(), text: '' }];
          }
        } else {
          resps = resps.map(r => {
            if (typeof r === 'string') return { _id: uid(), text: r };
            if (r && typeof r === 'object') return { _id: r._id || uid(), text: toStr(r.text) };
            return { _id: uid(), text: '' };
          });
        }
        return { ...proj, responsibilities: resps };
      });
      
      console.log('[useResumeForm] Draft validation and sanitization passed');
      return draft;
    } catch (error) {
      console.error('[useResumeForm] Error loading draft:', error);
      return defaultData;
    }
  };
  
  const [formData, setFormData]           = useState(loadSafeDraft);
  const [selectedTemplate, setSelectedTemplate] = useState('modernMinimal');
  const [atsResult, setAtsResult]         = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const [errors, setErrors]               = useState({});
  const [touched, setTouched]             = useState({});
  const [warnings, setWarnings]           = useState({});
  const [fieldLengths, setFieldLengths]   = useState({});

  const { lastSavedAt } = useResumeAutosave(formData);

  // Cloud sync removed to conserve free-tier limits as per architecture rules



  // Debounced ATS scoring
  useEffect(() => {
    const t = setTimeout(() => setAtsResult(computeAtsScore(formData)), 400);
    return () => clearTimeout(t);
  }, [formData]);

  // Re-validate touched fields on change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      const touchedFields = Object.keys(touched).filter(k => touched[k]);
      if (touchedFields.length > 0) {
        const validationErrors = validatePersonalInfo(formData);
        setErrors(validationErrors);
        
        // Detect issues (warnings)
        const newWarnings = {};
        Object.keys(formData.personal || {}).forEach(field => {
          const value = formData.personal[field];
          if (value && typeof value === 'string') {
            const issues = detectIssues(value);
            if (issues.length > 0) {
              newWarnings[field] = issues[0].message;
            }
          }
        });
        setWarnings(newWarnings);
      }
    }, 300); // Debounce validation
    
    return () => clearTimeout(timer);
  }, [formData.personal, touched]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track field lengths for character count
  useEffect(() => {
    const lengths = {};
    Object.keys(formData.personal || {}).forEach(field => {
      const value = formData.personal[field];
      if (typeof value === 'string') {
        lengths[field] = value.length;
      }
    });
    setFieldLengths(lengths);
  }, [formData.personal]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const toggleSkill = useCallback((category, skill) => {
    const strSkill = toStr(skill);
    if (!strSkill) return;
    setFormData(prev => {
      const catList = Array.isArray(prev.technicalSkills?.[category]) ? prev.technicalSkills[category].map(toStr) : [];
      return {
        ...prev,
        technicalSkills: {
          ...prev.technicalSkills,
          [category]: catList.includes(strSkill)
            ? catList.filter(s => s !== strSkill)
            : [...catList, strSkill],
        },
      };
    });
  }, []);

  const toggleTool = useCallback((category, tool) => {
    const strTool = toStr(tool);
    if (!strTool) return;
    setFormData(prev => {
      const catList = Array.isArray(prev.tools?.[category]) ? prev.tools[category].map(toStr) : [];
      return {
        ...prev,
        tools: {
          ...prev.tools,
          [category]: catList.includes(strTool)
            ? catList.filter(t => t !== strTool)
            : [...catList, strTool],
        },
      };
    });
  }, []);

  const toggleCompetency = useCallback((competency) => {
    const strComp = toStr(competency);
    if (!strComp) return;
    setFormData(prev => {
      const list = Array.isArray(prev.coreCompetencies) ? prev.coreCompetencies.map(toStr) : [];
      return {
        ...prev,
        coreCompetencies: list.includes(strComp)
          ? list.filter(c => c !== strComp)
          : [...list, strComp],
      };
    });
  }, []);

  const addItem = useCallback((section, item) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...(Array.isArray(prev[section]) ? prev[section] : []), { _id: uid(), ...item }],
    }));
  }, []);

  const removeItem = useCallback((section, id) => {
    setFormData(prev => ({
      ...prev,
      [section]: (Array.isArray(prev[section]) ? prev[section] : []).filter(item => item && item._id !== id),
    }));
  }, []);

  const updateItem = useCallback((section, id, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: (Array.isArray(prev[section]) ? prev[section] : []).map(item =>
        item && item._id === id ? { ...item, [field]: value } : item
      ),
    }));
  }, []);

  const addAchievement = useCallback((expId) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.map(exp =>
        exp._id === expId
          ? { ...exp, achievements: [...exp.achievements, { _id: uid(), text: '' }] }
          : exp
      ),
    }));
  }, []);

  const removeAchievement = useCallback((expId, achId) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.map(exp =>
        exp._id === expId
          ? { ...exp, achievements: exp.achievements.filter(a => a._id !== achId) }
          : exp
      ),
    }));
  }, []);

  const updateAchievement = useCallback((expId, achId, value) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.map(exp =>
        exp._id === expId
          ? { ...exp, achievements: exp.achievements.map(a => a._id === achId ? { ...a, text: value } : a) }
          : exp
      ),
    }));
  }, []);

  const handleBlur = useCallback((field, type = 'text') => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Sanitize the field value
    const currentValue = formData.personal?.[field];
    if (currentValue && typeof currentValue === 'string') {
      const sanitized = sanitizeInput(currentValue, type);
      if (sanitized !== currentValue) {
        setFormData(prev => ({
          ...prev,
          personal: { ...prev.personal, [field]: sanitized }
        }));
      }
    }
    
    // Validate
    const validationErrors = validatePersonalInfo(formData);
    setErrors(validationErrors);
  }, [formData]);

  const handlePreview = useCallback(async () => {
    try {
      await previewResumePDF(formData, selectedTemplate);
    } catch (err) {
      toast.error('Failed to generate preview.');
    }
  }, [formData, selectedTemplate]);

  const handlePrint = useCallback(async () => {
    try {
      await previewResumePDF(formData, selectedTemplate);
    } catch (err) {
      toast.error('Failed to prepare print view.');
    }
  }, [formData, selectedTemplate]);

  const downloadBackupHTML = useCallback(() => {
    // Deprecated with react-pdf, but keeping stub for compat
  }, [formData, selectedTemplate]);

  const downloadResume = useCallback(async () => {
    if (isDownloading) return;
    const errs = validatePersonalInfo(formData);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTouched({ name: true, email: true });
      const firstError = errs.name || errs.email || Object.values(errs)[0];
      toast.error(firstError);
      return false;
    }
    setIsDownloading(true);
    try {
      await downloadResumePDF(formData, selectedTemplate);
      toast.success('Your QA resume PDF has been downloaded!');
      
      trackResumeDownload({
        email:          formData.personal?.email,
        specialization: formData.personal?.jobTitle,
        atsScore:       atsResult?.total,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [formData, selectedTemplate, atsResult, isDownloading]);

  const injectKeyword = useCallback((keyword, { silent = false, aiCategory = null, isFromAI = false } = {}) => {
    const strKw = toStr(keyword);
    if (!strKw) return;
    setFormData(prev => {
      const comps = Array.isArray(prev.coreCompetencies) ? prev.coreCompetencies : [];
      const tech = prev.technicalSkills ? Object.values(prev.technicalSkills).flat() : [];
      const tls = prev.tools ? Object.values(prev.tools).flat() : [];
      
      const allSelected = [
        ...comps,
        ...tech,
        ...tls
      ].map(toStr).filter(Boolean).map(k => k.toLowerCase().trim());

      let finalKeyword = strKw.trim();
      
      // Attempt to find the exact casing from preset categories to prevent mismatch bugs
      const allSkillOpts = Object.values(SKILL_CATEGORIES || {}).flat();
      const matchSkill = allSkillOpts.find(o => o.toLowerCase() === finalKeyword.toLowerCase());
      if (matchSkill) finalKeyword = matchSkill;
      else {
        const allToolOpts = Object.values(TOOL_CATEGORIES || {}).flat();
        const matchTool = allToolOpts.find(o => o.toLowerCase() === finalKeyword.toLowerCase());
        if (matchTool) finalKeyword = matchTool;
      }

      if (allSelected.includes(finalKeyword.toLowerCase())) {
        return prev;
      }

      const nextAiInjected = isFromAI
        ? [...new Set([...(prev.aiInjectedKeywords || []), finalKeyword])]
        : (prev.aiInjectedKeywords || []);

      // 1. If the AI explicitly provided a category map
      if (aiCategory) {
        if (SKILL_CATEGORIES[aiCategory]) {
          if (!silent) toast.success(`"${finalKeyword}" added to Technical Skills!`);
          return {
            ...prev,
            aiInjectedKeywords: nextAiInjected,
            technicalSkills: {
              ...prev.technicalSkills,
              [aiCategory]: [...prev.technicalSkills[aiCategory], finalKeyword]
            }
          };
        }
        if (TOOL_CATEGORIES[aiCategory]) {
          if (!silent) toast.success(`"${finalKeyword}" added to QA Tools!`);
          return {
            ...prev,
            aiInjectedKeywords: nextAiInjected,
            tools: {
              ...prev.tools,
              [aiCategory]: [...prev.tools[aiCategory], finalKeyword]
            }
          };
        }
        if (aiCategory === 'competency') {
          if (!silent) toast.success(`"${finalKeyword}" added to Core Competencies!`);
          return { ...prev, aiInjectedKeywords: nextAiInjected, coreCompetencies: [...prev.coreCompetencies, finalKeyword] };
        }
      }

      // 2. Fallback to finding the matching category in technicalSkills
      const skillCat = Object.entries(SKILL_CATEGORIES || {}).find(([, opts]) =>
        opts.some(o => o.toLowerCase() === finalKeyword.toLowerCase())
      );
      if (skillCat) {
        if (!silent) toast.success(`"${finalKeyword}" added to Technical Skills!`);
        return {
          ...prev,
          aiInjectedKeywords: nextAiInjected,
          technicalSkills: {
            ...prev.technicalSkills,
            [skillCat[0]]: [...prev.technicalSkills[skillCat[0]], finalKeyword]
          }
        };
      }

      // 3. Fallback to finding the matching category in tools
      const toolCat = Object.entries(TOOL_CATEGORIES || {}).find(([, opts]) =>
        opts.some(o => o.toLowerCase() === finalKeyword.toLowerCase())
      );
      if (toolCat) {
        if (!silent) toast.success(`"${finalKeyword}" added to QA Tools!`);
        return {
          ...prev,
          aiInjectedKeywords: nextAiInjected,
          tools: {
            ...prev.tools,
            [toolCat[0]]: [...prev.tools[toolCat[0]], finalKeyword]
          }
        };
      }

      // 4. Default Fallback to core competencies
      if (!silent) toast.success(`"${finalKeyword}" added to Core Competencies!`);
      return { ...prev, aiInjectedKeywords: nextAiInjected, coreCompetencies: [...prev.coreCompetencies, finalKeyword] };
    });
  }, []);

  const validateStep = useCallback((stepId) => {
    if (stepId !== 'personal') return true;
    const errs = validatePersonalInfo(formData);
    const blockingErrors = {};
    if (errs.name)  blockingErrors.name  = errs.name;
    if (errs.email) blockingErrors.email = errs.email;
    if (Object.keys(blockingErrors).length > 0) {
      setErrors(errs);
      setTouched({ name: true, email: true });
      // Show the actual field error, not a generic message
      const firstError = blockingErrors.name || blockingErrors.email;
      toast.error(firstError);
      return false;
    }
    setErrors(errs);
    return true;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(makeDefaultFormData(userEmail));
  }, [userEmail]);

  return {
    formData,
    setFormData,
    selectedTemplate,
    setSelectedTemplate,
    atsResult,
    isDownloading,
    isPopupBlocked,
    setIsPopupBlocked,
    errors,
    touched,
    warnings,
    fieldLengths,
    lastSavedAt,
    maxLengths: MAX_LENGTHS,
    // mutations
    toggleSkill,
    toggleTool,
    toggleCompetency,
    addItem,
    removeItem,
    updateItem,
    addAchievement,
    removeAchievement,
    updateAchievement,
    handleBlur,
    handlePreview,
    handlePrint,
    downloadBackupHTML,
    downloadResume,
    validateStep,
    injectKeyword,
    resetForm,
  };
}
